import { fbLogin } from '@/lib/facebook';
import { supabase } from '@/lib/supabase';
import { callApi } from '@/utils/api';
import {
  getActiveTeamQueryKey,
  getFacebookAccountsQueryKey,
} from '@/utils/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTeams } from './useTeams';

export const useFacebookToken = () => {
  const queryClient = useQueryClient();
  const { activeTeam } = useTeams();

  const query = useQuery({
    queryKey: getFacebookAccountsQueryKey(),
    queryFn: async () => {
      const { data, error } = await callApi(supabase, `/facebook/accounts`, {
        method: 'GET',
      });
      if (error) throw error;

      return data;
    },
    enabled: !!activeTeam.data?.tokens?.facebook_token,
    retry: false,
  });

  const authenticate = useMutation({
    mutationFn: async () => {
      const response = await fbLogin();

      if (!response.authResponse?.accessToken) {
        throw new Error('Facebook authentication failed');
      }

      const { data, error } = await callApi(supabase, `/tokens`, {
        method: 'POST',
        data: {
          facebook_token: response.authResponse.accessToken,
        },
      });

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getFacebookAccountsQueryKey(),
      });
    },
  });

  const connectAccount = useMutation({
    mutationFn: async (accountId: string) => {
      const { data, error } = await callApi(
        supabase,
        `/facebook/accounts/connect`,
        {
          method: 'POST',
          data: {
            account_id: accountId,
          },
        },
      );

      if (error) throw error;

      return data;
    },
  });

  const disconnect = useMutation({
    mutationFn: async () => {
      const { error } = await callApi(supabase, '/tokens', {
        method: 'DELETE',
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getFacebookAccountsQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getActiveTeamQueryKey(),
      });
    },
  });

  return {
    ...query,
    authenticate,
    connectAccount,
    disconnect,
    needsReauthentication:
      query.isError || !activeTeam.data?.tokens?.facebook_token,
  };
};
